// src/components/layout/PageHeader.tsx
import React, { ReactNode } from 'react';
import { cn } from '../../utils/tw';

interface PageHeaderProps {
  /** Icon to display (e.g., <LayoutDashboard size={24} />) */
  icon?: ReactNode;
  /** Main page title */
  title: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Optional badge (e.g., <Badge>Live</Badge>) */
  badge?: ReactNode;
  /** Action buttons (right side) */
  actions?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Show border at bottom */
  showBorder?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  badge,
  actions,
  className,
  showBorder = true,
}) => {
  return (
    <div
      className={cn(
        'mb-6 flex items-center justify-between',
        showBorder && 'border-b border-gray-200 dark:border-dark-border pb-4',
        className
      )}
    >
      {/* Left Side: Icon, Title, Subtitle, Badge */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Icon */}
        {icon && (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-finance-500 to-finance-600 flex items-center justify-center text-white shadow-lg">
            {icon}
          </div>
        )}

        {/* Title & Subtitle */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
              {title}
            </h1>
            {badge && <div className="flex-shrink-0">{badge}</div>}
          </div>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-600 dark:text-dark-mutedText">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Right Side: Actions */}
      {actions && (
        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
